import { useLayoutEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, AppBar, Toolbar, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { LicenseInfo } from '@mui/x-license';

LicenseInfo.setLicenseKey('ae76c599367b237c357650e27c548556Tz0xMjM3NTQsRT0xNzk4ODQ3OTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y');
import { LoadScript } from '@react-google-maps/api';
import theme from './theme/theme';
import Start from './pages/Start';
import RegistrationForm from './pages/RegistrationForm';
import CaregiverForm from './pages/CaregiverForm/CaregiverForm';
import TemplateList from './pages/Templates/TemplateList';
import TemplateDetail from './pages/Templates/TemplateDetail';
import DocumentList from './pages/Documents/DocumentList';
import QuickEntry from './pages/QuickEntry/QuickEntry';
import QuickEntryLanding from './pages/QuickEntry/QuickEntryLanding';
import MarketerEntry from './pages/MarketerEntry/MarketerEntry';
import AdminSettings from './pages/AdminSettings/AdminSettings';
import LanguageSelector from './components/LanguageSelector';
import NavDrawer from './components/NavDrawer';
import { AuthProvider, useAuth } from './context/AuthContext';
import { clearStoredQuickEntryUser, setStoredQuickEntryUser } from './utils/quickEntryUser';
import { getLinkDestination, hasAutoLoginParams } from './utils/autoLoginLink';
import AvailabilityUpdate from './pages/Availability/AvailabilityUpdate';
import CaregiverHub from './pages/CaregiverHub/CaregiverHub';
import { CaregiverPortalProvider, useCaregiverPortal } from './context/CaregiverPortalContext';

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

// Public route - redirect authenticated users to their home based on role
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // A login link names who should be signed in; let the page replace any stored session
  // (e.g. a spouse's) instead of redirecting into it.
  if (isAuthenticated && !hasAutoLoginParams(location.search)) {
    if ((hasRole('marketer') || hasRole('recruiter')) && !hasRole('caregiver')) {
      return <Navigate to="/marketer-entry" replace />;
    }
    if (hasRole('quick-entry') && !hasRole('caregiver')) {
      return <Navigate to="/qe-landing" replace />;
    }
    return <Navigate to={(hasRole('caregiver') && getLinkDestination(location.search)) || '/caregivers'} replace />;
  }

  return <>{children}</>;
}

// Admin route - requires authentication + admin role
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/start" replace />;
  }

  if (!hasRole('admin')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography variant="h6" color="error">Access denied. Admin privileges required.</Typography>
      </Box>
    );
  }

  return <>{children}</>;
}

function RoleRoute({ roles, redirectTo = '/start', children }: { roles: string[]; redirectTo?: string; children: React.ReactNode }) {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) return <Navigate to="/start" replace />;
  if (!roles.some(r => hasRole(r))) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

// Caregiver pages past the application (hub, availability); anyone still on the form is sent back to it.
function SubmittedCaregiverRoute({ children }: { children: React.ReactNode }) {
  const { formSubmitted, loadFailed, refresh } = useCaregiverPortal();
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  // A failed lookup is not "form unfinished": offer a retry instead of sending a submitted caregiver to the form.
  if (loadFailed) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh" gap={2} px={2}>
        <Alert severity="error">{t('portal.loadFailed')}</Alert>
        <Button
          variant="contained"
          disabled={retrying}
          onClick={async () => {
            setRetrying(true);
            await refresh();
            setRetrying(false);
          }}
        >
          {retrying ? <CircularProgress size={22} color="inherit" /> : t('portal.retry')}
        </Button>
      </Box>
    );
  }
  if (formSubmitted === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!formSubmitted) return <Navigate to="/caregiverform" replace />;
  return <>{children}</>;
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (location.pathname === '/start') {
      if (searchParams.has('user')) {
        setStoredQuickEntryUser(searchParams.get('user'));
      } else {
        clearStoredQuickEntryUser();
      }
    } else if (location.pathname === '/quick-entry' && searchParams.has('user')) {
      setStoredQuickEntryUser(searchParams.get('user'));
    }
  }, [location.pathname, location.search]);

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <AppBar position="static" sx={{ backgroundColor: '#FFFFFF' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box
            component="img"
            src="/images/logo/evo-logo.png"
            alt="Evo Logo"
            sx={{
              maxHeight: 40,
              transition: 'transform 0.3s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageSelector />
            {isAuthenticated && (
              <IconButton
                edge="end"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: 2, color: '#330662' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
        <LoadScript
          googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={GOOGLE_MAPS_LIBRARIES}
          loadingElement={<></>}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/start" replace />} />
            <Route
              path="/start"
              element={
                <PublicRoute>
                  <Start />
                </PublicRoute>
              }
            />
            <Route path="/register" element={<RegistrationForm />} />
            <Route
              path="/caregiverform"
              element={
                <RoleRoute roles={['caregiver', 'admin']} redirectTo="/qe-landing">
                  <CaregiverForm />
                </RoleRoute>
              }
            />
            <Route
              path="/caregivers"
              element={
                <RoleRoute roles={['caregiver', 'admin']} redirectTo="/qe-landing">
                  <SubmittedCaregiverRoute><CaregiverHub /></SubmittedCaregiverRoute>
                </RoleRoute>
              }
            />
            <Route
              path="/availability"
              element={
                <RoleRoute roles={['caregiver', 'admin']} redirectTo="/qe-landing">
                  <SubmittedCaregiverRoute><AvailabilityUpdate /></SubmittedCaregiverRoute>
                </RoleRoute>
              }
            />
            <Route path="/templates" element={<AdminRoute><TemplateList /></AdminRoute>} />
            <Route path="/templates/:templateId" element={<AdminRoute><TemplateDetail /></AdminRoute>} />
            <Route path="/documents" element={<AdminRoute><DocumentList /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
            <Route path="/qe-landing" element={<RoleRoute roles={['quick-entry','admin']} redirectTo="/caregiverform"><QuickEntryLanding /></RoleRoute>} />
            <Route path="/quick-entry" element={<QuickEntry />} />
            <Route path="/marketer-entry" element={<RoleRoute roles={['marketer','recruiter','admin']} redirectTo="/start"><MarketerEntry /></RoleRoute>} />
          </Routes>
        </LoadScript>
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/start" replace />} />
          <Route
            path="/start"
            element={
              <PublicRoute>
                <Start />
              </PublicRoute>
            }
          />
          <Route path="/register" element={<RegistrationForm />} />
          <Route
            path="/caregiverform"
            element={
              <RoleRoute roles={['caregiver', 'admin']}>
                <CaregiverForm />
              </RoleRoute>
            }
          />
          <Route
            path="/caregivers"
            element={
              <RoleRoute roles={['caregiver', 'admin']}>
                <SubmittedCaregiverRoute><CaregiverHub /></SubmittedCaregiverRoute>
              </RoleRoute>
            }
          />
          <Route
            path="/availability"
            element={
              <RoleRoute roles={['caregiver', 'admin']}>
                <SubmittedCaregiverRoute><AvailabilityUpdate /></SubmittedCaregiverRoute>
              </RoleRoute>
            }
          />
          <Route path="/templates" element={<AdminRoute><TemplateList /></AdminRoute>} />
          <Route path="/templates/:templateId" element={<AdminRoute><TemplateDetail /></AdminRoute>} />
          <Route path="/documents" element={<AdminRoute><DocumentList /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
          <Route path="/qe-landing" element={<RoleRoute roles={['quick-entry','admin']} redirectTo="/caregiverform"><QuickEntryLanding /></RoleRoute>} />
          <Route path="/quick-entry" element={<QuickEntry />} />
          <Route path="/marketer-entry" element={<RoleRoute roles={['marketer','recruiter','admin']} redirectTo="/start"><MarketerEntry /></RoleRoute>} />
        </Routes>
      )}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <CaregiverPortalProvider>
          <AppContent />
        </CaregiverPortalProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
